import React, { useEffect } from "react";
import "../css/components/PrivacyPolicy&TermsOfUse.css";

import headerpic from "../assets/header-images/headerpic.png";
import privacy from "../assets/header-images/privacy.jpg";

const PrivacyPolicy = () => {
  const lastUpdated = "December 15, 2024";

  useEffect(() => {
    // Add scroll progress indicator
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;

      const statusBar = document.querySelector(".status-bar");
      if (statusBar) {
        statusBar.style.transform = `scaleX(${scrollPercent / 100})`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <div className="status-bar"></div>
      <div className="privacy-background-page">
        <div className="privacy-policy-container">
          {/* Enhanced Header Section */}
          <div className="policy-header">
            <h1 className="privacy-policy-title">Privacy Policy</h1>
            <p className="privacy-policy-date">Last Updated: {lastUpdated}</p>
            <p className="privacy-policy-intro">
              At CollegeNav, we are committed to protecting your privacy and
              ensuring the security of your personal information. This
              comprehensive Privacy Policy explains how we collect, use,
              process, and safeguard your data when you use our educational
              platform. By accessing or using CollegeNav, you acknowledge that
              you have read, understood, and agree to be bound by the terms of
              this Privacy Policy. For any questions or concerns regarding your
              privacy, please contact our dedicated privacy team at{" "}
              <a
                href="mailto:collegenav.info@gmail.com"
                className="privacy-policy-email"
              >
                collegenav.info@gmail.com
              </a>
              .
            </p>
          </div>

          {/* Enhanced Table of Contents */}
          <div className="table-of-contents">
            <h3 className="toc-title">Table of Contents</h3>
            <ul className="toc-list">
              <li className="toc-item">
                <a
                  href="#section-1"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-1");
                  }}
                >
                  1. Information We Collect
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-2"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-2");
                  }}
                >
                  2. How We Use Your Information
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-3"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-3");
                  }}
                >
                  3. Legal Basis for Processing
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-4"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-4");
                  }}
                >
                  4. Information Sharing & Disclosure
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-5"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-5");
                  }}
                >
                  5. International Data Transfers
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-6"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-6");
                  }}
                >
                  6. Your Privacy Rights & Choices
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-7"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-7");
                  }}
                >
                  7. Data Security & Protection
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-8"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-8");
                  }}
                >
                  8. Children's Privacy Protection
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-9"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-9");
                  }}
                >
                  9. Data Retention & Deletion
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-10"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-10");
                  }}
                >
                  10. Policy Updates & Changes
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-11"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-11");
                  }}
                >
                  11. Contact Information
                </a>
              </li>
            </ul>
          </div>

          {/* Enhanced Content Sections */}
          <section id="section-1" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="01">
              1. Information We Collect
            </h2>
            <p className="privacy-policy-text">
              We collect various types of information to provide and improve our
              educational services. This information helps us personalize your
              experience and ensure the security of our platform.
            </p>
            <ul className="privacy-policy-list">
              <li className="privacy-policy-item">
                <strong>Personal Identification Information:</strong> When you
                create an account, we collect essential information including
                your full name, email address, secure password, date of birth,
                educational background, and any additional profile information
                you choose to provide to enhance your CollegeNav experience.
              </li>
              <li className="privacy-policy-item">
                <strong>Usage and Analytics Data:</strong> We automatically
                collect detailed information about your interactions with our
                platform, including IP addresses, browser types and versions,
                device information, access times, pages viewed, click patterns,
                referring URLs, and navigation paths to improve our services.
              </li>
              <li className="privacy-policy-item">
                <strong>Cookies and Tracking Technologies:</strong> We utilize
                cookies, web beacons, pixel tags, and similar tracking
                technologies to enhance your browsing experience, remember your
                preferences, analyze usage patterns, and provide personalized
                content and advertisements.
              </li>
              <li className="privacy-policy-item">
                <strong>Device and Technical Information:</strong> We collect
                comprehensive device information including hardware model,
                operating system version, unique device identifiers, mobile
                network information, and technical specifications to optimize
                our platform's performance across different devices.
              </li>
              <li className="privacy-policy-item">
                <strong>Educational and Academic Data:</strong> With your
                consent, we may collect information about your academic
                interests, test scores, college preferences, application status,
                and educational goals to provide tailored recommendations and
                guidance.
              </li>
            </ul>
          </section>

          <section id="section-2" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="02">
              2. How We Use Your Information
            </h2>
            <p className="privacy-policy-text">
              We use the collected information for various legitimate purposes
              to enhance your educational journey and improve our services. Our
              data usage is always aligned with your expectations and privacy
              rights.
            </p>
            <ul className="privacy-policy-list">
              <li className="privacy-policy-item">
                <strong>Service Provision and Enhancement:</strong> We use your
                information to operate, maintain, and continuously improve the
                functionality, performance, and user experience of the
                CollegeNav platform, ensuring reliable access to our educational
                resources.
              </li>
              <li className="privacy-policy-item">
                <strong>Personalization and Recommendations:</strong> We analyze
                your preferences, academic interests, and usage patterns to
                provide personalized content, tailored college recommendations,
                customized study plans, and relevant educational opportunities
                that match your goals.
              </li>
              <li className="privacy-policy-item">
                <strong>Communication and Support:</strong> We use your contact
                information to send important service updates, educational
                newsletters, promotional materials (with your consent), respond
                to your inquiries, and provide customer support when needed.
              </li>
              <li className="privacy-policy-item">
                <strong>Security and Fraud Prevention:</strong> We monitor and
                analyze usage patterns to detect suspicious activities, prevent
                unauthorized access, protect against fraud, ensure platform
                security, and maintain the integrity of our educational
                community.
              </li>
              <li className="privacy-policy-item">
                <strong>Analytics and Research:</strong> We use aggregated and
                anonymized data to conduct research, analyze usage trends,
                understand user behavior, measure the effectiveness of our
                services, and develop new features and educational tools.
              </li>
              <li className="privacy-policy-item">
                <strong>Legal Compliance:</strong> We process your information
                as necessary to comply with applicable laws, regulations, legal
                processes, and governmental requests, and to enforce our terms
                of service and protect our rights and those of our users.
              </li>
            </ul>
          </section>

          <section id="section-3" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="03">
              3. Legal Basis for Processing
            </h2>
            <p className="privacy-policy-text">
              We process your personal information based on several legal
              grounds, ensuring that our data processing activities are lawful,
              fair, and transparent. The specific legal basis depends on the
              type of information and the context of processing.
            </p>
            <ul className="privacy-policy-list">
              <li className="privacy-policy-item">
                <strong>Consent:</strong> When you voluntarily provide
                information or agree to specific data processing activities,
                such as receiving marketing communications or participating in
                surveys, we rely on your explicit consent as the legal basis for
                processing.
              </li>
              <li className="privacy-policy-item">
                <strong>Contractual Necessity:</strong> We process your personal
                information to fulfill our contractual obligations to you,
                including providing access to our educational platform,
                delivering requested services, and maintaining your account.
              </li>
              <li className="privacy-policy-item">
                <strong>Legal Obligations:</strong> We process your personal
                information when required to comply with applicable laws,
                regulations, court orders, or other legal obligations, such as
                tax reporting or responding to lawful government requests.
              </li>
              <li className="privacy-policy-item">
                <strong>Legitimate Interests:</strong> We process your personal
                information to pursue our legitimate business interests, such as
                improving our services, ensuring security, conducting analytics,
                and developing new features, while ensuring these interests do
                not override your privacy rights.
              </li>
              <li className="privacy-policy-item">
                <strong>Vital Interests:</strong> In rare circumstances, we may
                process your information to protect your vital interests or
                those of another person, such as in emergency situations or to
                prevent harm.
              </li>
            </ul>
          </section>

          <section id="section-4" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="04">
              4. Information Sharing & Disclosure
            </h2>
            <p className="privacy-policy-text">
              We are committed to protecting your privacy and do not sell your
              personal information to third parties. We only share your
              information in specific circumstances and with appropriate
              safeguards in place.
            </p>
            <ul className="privacy-policy-list">
              <li className="privacy-policy-item">
                <strong>Trusted Service Providers:</strong> We may share your
                information with carefully vetted third-party service providers
                who assist us in operating our platform, such as cloud hosting
                services, payment processors, analytics providers, and customer
                support tools, all bound by strict confidentiality agreements.
              </li>
              <li className="privacy-policy-item">
                <strong>Educational Partners:</strong> With your explicit
                consent, we may share relevant information with colleges,
                universities, scholarship providers, or other educational
                institutions to facilitate applications, provide
                recommendations, or offer educational opportunities.
              </li>
              <li className="privacy-policy-item">
                <strong>Legal Requirements:</strong> We may disclose your
                information when required by law, court order, or government
                regulation, or when we believe in good faith that disclosure is
                necessary to protect our rights, your safety, or the safety of
                others, or to investigate fraud or security issues.
              </li>
              <li className="privacy-policy-item">
                <strong>Business Transfers:</strong> In the event of a merger,
                acquisition, reorganization, or sale of assets, your information
                may be transferred to the acquiring entity, subject to the same
                privacy protections outlined in this policy.
              </li>
              <li className="privacy-policy-item">
                <strong>Aggregated and Anonymized Data:</strong> We may share
                aggregated, anonymized, or de-identified information that cannot
                reasonably be used to identify you for research, analytics, or
                business purposes.
              </li>
            </ul>
          </section>

          <section id="section-5" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="05">
              5. International Data Transfers
            </h2>
            <p className="privacy-policy-text">
              CollegeNav operates globally, and your information may be
              transferred to, stored, and processed in countries other than your
              country of residence. We ensure that all international transfers
              of personal data are conducted with appropriate safeguards and in
              compliance with applicable data protection laws.
            </p>
            <p className="privacy-policy-text">
              When we transfer your personal information outside your country,
              we implement appropriate safeguards such as standard contractual
              clauses, adequacy decisions, or other legally recognized transfer
              mechanisms to ensure your data receives adequate protection. If
              you are located in the European Economic Area (EEA) or other
              regions with specific data protection requirements, we ensure
              compliance with applicable cross-border data transfer regulations.
            </p>
          </section>

          <section id="section-6" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="06">
              6. Your Privacy Rights & Choices
            </h2>
            <p className="privacy-policy-text">
              We respect your privacy rights and provide you with various
              options to control how your personal information is collected,
              used, and shared. The specific rights available to you may vary
              depending on your location and applicable laws.
            </p>
            <ul className="privacy-policy-list">
              <li className="privacy-policy-item">
                <strong>Access and Portability:</strong> You have the right to
                access your personal information and request a copy of the data
                we hold about you in a structured, machine-readable format. You
                can also request that we transfer your data to another service
                provider where technically feasible.
              </li>
              <li className="privacy-policy-item">
                <strong>Correction and Updates:</strong> You can access and
                update your personal information through your account settings
                at any time. If you notice any inaccuracies or outdated
                information, you can correct it directly or contact us for
                assistance.
              </li>
              <li className="privacy-policy-item">
                <strong>Deletion and Erasure:</strong> You have the right to
                request the deletion of your personal information under certain
                circumstances. You can delete your account or contact us at{" "}
                <a
                  href="mailto:collegenav.info@gmail.com"
                  className="privacy-policy-email"
                >
                  collegenav.info@gmail.com
                </a>{" "}
                to request data deletion, subject to legal retention
                requirements.
              </li>
              <li className="privacy-policy-item">
                <strong>Processing Restrictions:</strong> You have the right to
                request the restriction of processing of your personal
                information under certain circumstances, such as when you
                contest the accuracy of the data or object to processing based
                on legitimate interests.
              </li>
              <li className="privacy-policy-item">
                <strong>Objection Rights:</strong> You have the right to object
                to the processing of your personal information based on
                legitimate interests, direct marketing, or profiling. We will
                cease processing unless we have compelling legitimate grounds
                that override your interests.
              </li>
              <li className="privacy-policy-item">
                <strong>Communication Preferences:</strong> You can opt-out of
                receiving promotional emails by following the unsubscribe
                instructions in those emails or by updating your communication
                preferences in your account settings. Note that you may still
                receive important service-related communications.
              </li>
              <li className="privacy-policy-item">
                <strong>Cookie Controls:</strong> You can manage your cookie
                preferences through your browser settings or our cookie
                preference center. Note that disabling certain cookies may
                affect the functionality of our platform.
              </li>
            </ul>
          </section>

          <section id="section-7" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="07">
              7. Data Security & Protection
            </h2>
            <p className="privacy-policy-text">
              We implement comprehensive security measures to protect your
              personal information from unauthorized access, alteration,
              disclosure, or destruction. Our security practices are designed to
              safeguard your data throughout its lifecycle on our platform.
            </p>
            <p className="privacy-policy-text">
              Our security measures include industry-standard encryption for
              data in transit and at rest, secure authentication protocols,
              regular security audits and assessments, access controls and
              monitoring systems, employee training on data protection, and
              incident response procedures. However, no method of transmission
              over the internet or electronic storage is completely secure, and
              we cannot guarantee absolute security. We encourage you to use
              strong passwords and keep your account credentials confidential.
            </p>
          </section>

          <section id="section-8" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="08">
              8. Children's Privacy Protection
            </h2>
            <p className="privacy-policy-text">
              CollegeNav is committed to protecting the privacy of children and
              complying with applicable children's privacy laws, including the
              Children's Online Privacy Protection Act (COPPA) in the United
              States and similar regulations worldwide.
            </p>
            <p className="privacy-policy-text">
              Our services are primarily designed for students aged 13 and
              older. We do not knowingly collect personal information from
              children under 13 without verifiable parental consent. If we
              become aware that we have collected personal information from a
              child under 13 without proper consent, we will take immediate
              steps to delete such information. Parents and guardians who
              believe their child has provided personal information to us can
              contact us to request review and deletion of such information.
            </p>
          </section>

          <section id="section-9" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="09">
              9. Data Retention & Deletion
            </h2>
            <p className="privacy-policy-text">
              We retain your personal information only for as long as necessary
              to fulfill the purposes for which it was collected, comply with
              legal obligations, resolve disputes, and enforce our agreements.
              Our retention periods are based on the type of information, the
              purpose for processing, and applicable legal requirements.
            </p>
            <p className="privacy-policy-text">
              Generally, we retain account information for the duration of your
              active account and for a reasonable period thereafter to comply
              with legal obligations and resolve any disputes. Usage data and
              analytics information may be retained in aggregated or anonymized
              form for longer periods for research and improvement purposes.
              When we no longer need your personal information, we securely
              delete or anonymize it in accordance with our data retention and
              deletion policies.
            </p>
          </section>

          <section id="section-10" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="10">
              10. Policy Updates & Changes
            </h2>
            <p className="privacy-policy-text">
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, services, legal requirements, or other
              operational, legal, or regulatory reasons. We are committed to
              keeping you informed about any material changes to how we handle
              your personal information.
            </p>
            <p className="privacy-policy-text">
              When we make significant changes to this Privacy Policy, we will
              notify you through prominent notices on our website, email
              notifications, or other appropriate communication methods. We
              encourage you to review this Privacy Policy periodically to stay
              informed about our privacy practices. Your continued use of our
              services after any changes indicates your acceptance of the
              updated Privacy Policy.
            </p>
          </section>

          <section id="section-11" className="privacy-policy-section">
            <h2 className="privacy-policy-heading" data-section="11">
              11. Contact Information
            </h2>
            <p className="privacy-policy-text">
              We value your privacy concerns and are committed to addressing any
              questions or issues you may have regarding this Privacy Policy or
              our data practices. Our dedicated privacy team is available to
              assist you with any privacy-related matters.
            </p>
            <p className="privacy-policy-text">
              If you have any questions, concerns, or requests regarding this
              Privacy Policy, your personal information, or our privacy
              practices, please don't hesitate to contact us at{" "}
              <a
                href="mailto:collegenav.info@gmail.com"
                className="privacy-policy-email"
              >
                collegenav.info@gmail.com
              </a>
              . We strive to respond to all privacy inquiries promptly and
              thoroughly. For users in certain jurisdictions, you may also have
              the right to lodge a complaint with your local data protection
              authority.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
